import { useState } from 'react';
import { precalcTests } from '../data';
import StateTestCard from '../components/StateTestCard';

export default function PrecalcTestsPage() {
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const navigate = window.REACT_APP_NAVIGATE;

  const selected = precalcTests.find(t => t.id === selectedTest);

  if (selected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 py-12">
        <StateTestCard test={selected} onBack={() => setSelectedTest(null)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700 text-white py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <button
            onClick={() => navigate('/grades/grade-12')}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Precalculus
          </button>
          <h1 className="text-5xl font-bold mb-6">Precalculus State Practice Tests</h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Authentic state assessments covering all Precalculus topics — functions, trigonometry,
            conics, sequences, vectors, and limits. Each test has 30 questions with full solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-lg">
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-2xl"></i>
              <span>150 Total Questions</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-book-open-fill text-2xl"></i>
              <span>Complete Solutions</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-bar-chart-fill text-2xl"></i>
              <span>Instant Scoring</span>
            </div>
          </div>
        </div>
      </div>

      {/* Teacher Message */}
      <div className="max-w-6xl mx-auto px-4 mt-12 mb-10">
        <div className="bg-white rounded-2xl shadow-sm p-8 border-l-4 border-teal-500">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-smile-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Message from Mr. Augustine</h3>
              <p className="text-slate-600 leading-relaxed">
                These five state practice tests represent the full breadth of Precalculus — from advanced
                function analysis and trigonometric identities to conic sections, sequences, vectors, and
                an introduction to limits. Work through each test carefully, show your reasoning, and use
                the step-by-step solutions to understand every concept deeply. Precalculus is your bridge
                to calculus and beyond — master it here!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Cards */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Choose Your State Test</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {precalcTests.map((test) => (
            <button
              key={test.id}
              onClick={() => setSelectedTest(test.id)}
              className="text-left bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all p-6 border-2 border-slate-100 hover:border-teal-300 group cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{test.flag}</span>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 group-hover:text-teal-600 transition-colors">
                      {test.state}
                    </h3>
                    <p className="text-sm text-slate-500">Precalculus Practice Test</p>
                  </div>
                </div>
                <div className="w-10 h-10 bg-slate-100 group-hover:bg-teal-100 rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-arrow-right-line text-xl text-slate-500 group-hover:text-teal-600 group-hover:translate-x-1 transition-all"></i>
                </div>
              </div>
              <p className="text-slate-600 mb-4 leading-relaxed text-sm">{test.description}</p>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-line"></i>
                  30 Questions
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-line"></i>
                  60 Points
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  ~90 min
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Standards Coverage */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Topics Covered Across All Tests</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-function-line', color: 'bg-teal-600', title: 'Functions & Inverses', desc: 'Composition, inverses, transformations, difference quotient, piecewise functions' },
              { icon: 'ri-compass-3-line', color: 'bg-emerald-600', title: 'Trigonometry', desc: 'Identities, equations, sum/difference formulas, double-angle, inverse trig' },
              { icon: 'ri-shape-line', color: 'bg-cyan-600', title: 'Conic Sections', desc: 'Circles, parabolas, ellipses, hyperbolas — equations, foci, vertices, eccentricity' },
              { icon: 'ri-line-chart-line', color: 'bg-teal-700', title: 'Exponential & Logarithmic', desc: 'Growth/decay models, natural log, solving equations, real-world applications' },
              { icon: 'ri-list-ordered', color: 'bg-emerald-700', title: 'Sequences & Series', desc: 'Arithmetic, geometric, sigma notation, Binomial Theorem, infinite series' },
              { icon: 'ri-arrow-right-up-line', color: 'bg-cyan-700', title: 'Vectors & Limits', desc: 'Dot product, magnitude, parametric equations, limit laws, continuity' },
            ].map((item, i) => (
              <div key={i} className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6 border border-teal-100">
                <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center mb-4`}>
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-6 text-center">Test-Taking Strategies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: 'ri-lightbulb-line', title: 'Identify the Topic', desc: 'Before solving, identify which concept applies — trig identity, limit law, conic form, etc.' },
              { icon: 'ri-calculator-line', title: 'Show All Steps', desc: 'Write out every step. Partial credit comes from correct process, not just the final answer.' },
              { icon: 'ri-checkbox-line', title: 'Check Your Work', desc: 'Substitute answers back. For logs, verify the domain. For trig, check the quadrant.' },
              { icon: 'ri-time-line', title: 'Pace Yourself', desc: 'Spend ~3 minutes per question. Skip hard ones and return with fresh eyes.' },
            ].map((tip, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <i className={`${tip.icon} text-2xl mt-1 flex-shrink-0`}></i>
                  <div>
                    <h3 className="font-bold text-lg mb-2">{tip.title}</h3>
                    <p className="text-white/85 text-sm leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
